import { forwardRef, memo, useCallback, useContext, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import ReplyIcon from '@mui/icons-material/Reply';
import { Bookmark, MoreHorizontal } from 'react-feather';
import { usePopper } from 'react-popper';
import { useIsFullPageChat } from '@/components/Chat/hooks';
import { PopperMenu } from '@/components/Popper';
import { Portal } from '@/components/Portal';
import { useZIndexModifier } from '@/components/presentation/popper-modifiers';
import { ReportFormat } from '@/enums/reports';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useAppSelector } from '@/store';
import { buildBrandInsightsChatLink } from '@/utils';
import { ChatHistoryItemContext } from './context';
import { useToggleChatBookmark } from './hooks';
import { useDownloadChatInstance } from './hooks/useDownloadChatInstance';
import { useDeleteChatModal } from './Modal.DeleteChat';
import { useRenameChatModal } from './Modal.RenameChat';
import { useShareChatModal } from './Modal.ShareChat';

type Props = {
  open: boolean;
  refElement: HTMLDivElement;
  setOpen: (open: boolean) => void;
};

type MenuAnchorProps = {
  className?: string;
  open: boolean;
  onClick: (e: React.MouseEvent) => void;
};

export const ChatActionsAnchor = memo(forwardRef<HTMLDivElement, MenuAnchorProps>(({ className, open, onClick }, ref) => (
  <div
    className={className}
    ref={ref}
    onClick={onClick}>
    <Anchor
      open={open} />
  </div>
)));

export const ChatActionsMenu = ({ open, refElement, setOpen }: Props) => {

  const [popperElement, setPopperElement] = useState<HTMLDivElement>(null);
  const { item } = useContext(ChatHistoryItemContext);

  const zIndexModifier = useZIndexModifier();

  const { styles: popperStyles, attributes: popperAttributes } = usePopper(refElement, popperElement, {
    modifiers: [
      zIndexModifier,
    ],
    placement: 'bottom-start',
  });
  const disableActions = useAppSelector(state => state.group.features.deleteChatsOnSessionEnd);

  const [toggleRenameModal, RenameModal] = useRenameChatModal();
  const [toggleDeleteModal, DeleteModal] = useDeleteChatModal();
  const [toggleShareModal, ShareModal] = useShareChatModal();
  const { download } = useDownloadChatInstance({
    chatInstanceId: item.id,
    name: item.name,
  });
  const copy = useCopyToClipboard();
  const [copied, setCopied] = useIconFlipper(2500);
  const toggleBookmarkMutation = useToggleChatBookmark({ chatInstanceId: item.id });

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleRenameClick = useCallback((_e: React.MouseEvent) => {
    toggleRenameModal();
    setOpen(false);
  }, [setOpen, toggleRenameModal]);

  const handleDeleteClick = useCallback((_e: React.MouseEvent) => {
    toggleDeleteModal();
    setOpen(false);
  }, [setOpen, toggleDeleteModal]);

  const handleShareClick = useCallback((_e: React.MouseEvent) => {
    toggleShareModal();
    setOpen(false);
  }, [setOpen, toggleShareModal]);

  const handleDownloadClick = useCallback((_e: React.MouseEvent) => {
    download({
      title: `Generating Export`,
      name: item.name,
      extension: ReportFormat.Word,
    });
    setOpen(false);
  }, [download, item.name, setOpen]);

  const isStandaloneChat = useIsFullPageChat();

  const handleCopyClick = useCallback((_e: React.MouseEvent) => {
    copy(buildBrandInsightsChatLink(({
      chatIdentifier: item.identifier,
      isStandaloneChat,
    })));
    setCopied();
  }, [copy, isStandaloneChat, item.identifier, setCopied]);

  const handleBookmarkClick = useCallback(() => {
    toggleBookmarkMutation.mutate(!item.bookmarked);
    setOpen(false);
  }, [item.bookmarked, setOpen, toggleBookmarkMutation]);

  return (
    <>
      {open && (
        <Portal>
          <ClickAwayListener
            onClickAway={handleClose}>
            <div
              ref={setPopperElement}
              style={popperStyles.popper}
              {...popperAttributes.popper}>
              <PopperMenu>
                <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleBookmarkClick}>
                  <StyledBookmarkIcon size={24} /> {item.bookmarked ? `Unbookmark` : `Bookmark`}
                </StyledPopperMenuItem>
                {!item.shared && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleRenameClick}>
                    <StyledRenameIcon /> Rename
                  </StyledPopperMenuItem>
                )}
                <StyledPopperMenuItem onClick={handleDownloadClick}>
                  <StyledDownloadIcon /> Download
                </StyledPopperMenuItem>
                {!item.shared && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleShareClick}>
                    <StyledShareIcon /> Share
                  </StyledPopperMenuItem>
                )}
                <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleCopyClick}>
                  {copied
                    ? <StyledDoneIcon />
                    : <StyledLinkIcon />}
                  Copy link
                </StyledPopperMenuItem>
                {!item.shared && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleDeleteClick}>
                    <StyledDeleteIcon /> Delete
                  </StyledPopperMenuItem>
                )}
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
      <ShareModal chatInstance={item} />
      <DeleteModal chatInstance={item} />
      <RenameModal chatInstance={item} />
    </>
  );
};

type AnchorProps = {
  open: boolean;
};

const Anchor = memo(forwardRef<HTMLDivElement, AnchorProps>(({ open }, ref) => {
  return (
    <AnchorRoot open={open} ref={ref}>
      <StyledMoreHorizontal />
    </AnchorRoot>
  );
}));

const AnchorRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'open',
})<AnchorProps>(({ theme, open }) => ({
  cursor: 'pointer',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  color: open ? theme.palette.black.light1 : undefined,
  backgroundColor: open ? theme.palette.gray.light2 : undefined,

  ':hover': {
    color: theme.palette.black.light1,
    backgroundColor: theme.palette.gray.light2,
  },
}));

const StyledMoreHorizontal = styled(MoreHorizontal)({
  width: 25,
  height: 25,
  padding: 5,
  boxSizing: 'border-box',
});

const iconMargin = 8;

const StyledRenameIcon = styled(DriveFileRenameOutlineIcon)(({ theme }) => ({
  marginRight: iconMargin,
}));

const StyledDeleteIcon = styled(DeleteIcon)(({ theme }) => ({
  marginRight: iconMargin,
}));

const StyledDownloadIcon = styled(FileDownloadIcon)(({ theme }) => ({
  marginRight: iconMargin,
}));

const StyledLinkIcon = styled(LinkIcon)(({ theme }) => ({
  marginRight: iconMargin,
}));

const StyledShareIcon = styled(ReplyIcon)(({ theme }) => ({
  transform: 'scaleX(-1)',
  marginRight: iconMargin,
}));

const StyledDoneIcon = styled(DoneIcon)(({ theme }) => ({
  color: theme.palette.green.main,
  marginRight: iconMargin,
}));

const StyledBookmarkIcon = styled(Bookmark)(({ theme }) => ({
  color: theme.palette.black.light1,
  marginRight: iconMargin,
}));

type PopperMenuItemProps = {
  disabled?: boolean;
};

const StyledPopperMenuItem = styled.div<PopperMenuItemProps>(({ disabled, theme }) => {
  const color = disabled ? theme.palette.gray.main : theme.palette.black.light1;
  return {
    display: 'flex',
    fontSize: 14,
    alignItems: 'center',
    cursor: disabled ? 'default' : 'pointer',
    color,
    height: 40,
    boxSizing: 'border-box',
    padding: '0 20px',

    [String(StyledRenameIcon)]: {
      color,
    },
    [String(StyledDeleteIcon)]: {
      color,
    },
    [String(StyledDownloadIcon)]: {
      color,
    },
    [String(StyledLinkIcon)]: {
      color,
    },
    [String(StyledShareIcon)]: {
      color,
    },
    [String(StyledBookmarkIcon)]: {
      color,
    },

    '&:hover': disabled ? undefined : {
      color: theme.palette.sentiment.primary.main,

      [String(StyledRenameIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledDeleteIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledDownloadIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledLinkIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledShareIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledBookmarkIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
    },
  };
});

// eslint-disable-next-line react-refresh/only-export-components
export function useIconFlipper(timeoutMs: number) {
  const [flipped, setFlipped] = useState(false);
  const timer = useRef<NodeJS.Timeout>();

  const handleSetFlipped = useCallback(() => {
    setFlipped(true);

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setFlipped(false);
    }, timeoutMs);
  }, [timeoutMs]);

  return [flipped, handleSetFlipped] as const;
}